import { useEffect, useState } from 'react';
import { useAudioDevices } from '../../hooks/useAudioDevices';
import { usePhoneStore } from '../../stores/phone-store';
import { primeDeviceLabels } from '../../lib/telephony/devices';
import { playTestTone } from '../../lib/telephony/audio';
import { emitEvent } from '../../lib/telephony';

type TestTarget = 'mic' | 'speaker' | 'ringtone';

/**
 * Mic + speaker selector. Persists selection via phone-store → Supabase.
 *
 * Testknoppen zijn eerlijk: de speaker-/beltoontest speelt strikt op het
 * gekozen apparaat (via de gedeelde tone-player met element-sink) en meldt
 * het als dat niet lukt. De oude tester viel stilletjes terug op het
 * default-apparaat, waardoor hij "werkte" maar nooit het gekozen apparaat
 * bewees. De beltoontest speelt exact de echte beltoon op het echte
 * beltoon-apparaat en -volume.
 */
export function DeviceSelector() {
  const { microphones, speakers, supported, refresh } = useAudioDevices();
  const microphone = usePhoneStore((s) => s.microphone);
  const speaker = usePhoneStore((s) => s.speaker);
  const ringtoneSpeaker = usePhoneStore((s) => s.ringtoneSpeaker);
  const ringtoneVolume = usePhoneStore((s) => s.ringtoneVolume);
  const setMicrophone = usePhoneStore((s) => s.setMicrophone);
  const setSpeaker = usePhoneStore((s) => s.setSpeaker);
  const setRingtoneSpeaker = usePhoneStore((s) => s.setRingtoneSpeaker);
  const setRingtoneVolume = usePhoneStore((s) => s.setRingtoneVolume);

  const [permissionPrimed, setPermissionPrimed] = useState(false);
  const [testing, setTesting] = useState<TestTarget | null>(null);
  const [testResult, setTestResult] = useState<{
    target: TestTarget;
    ok: boolean;
  } | null>(null);
  const [micLevel, setMicLevel] = useState(0);

  // Ask for permission once so labels become visible.
  useEffect(() => {
    const hasLabels = microphones.some((d) => d.label);
    if (!hasLabels && !permissionPrimed) {
      void primeDeviceLabels().then((ok) => {
        if (ok) {
          setPermissionPrimed(true);
          refresh();
        }
      });
    } else if (hasLabels) {
      setPermissionPrimed(true);
    }
  }, [microphones, permissionPrimed, refresh]);

  async function testMicrophone(deviceId: string | null) {
    if (!supported) return;
    setTesting('mic');
    setTestResult(null);
    setMicLevel(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      });
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const start = Date.now();
      let sawSignal = false;

      const loop = () => {
        analyser.getByteTimeDomainData(buf);
        let peak = 0;
        for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
        if (peak / 128 > 0.02) sawSignal = true;
        setMicLevel(peak / 128);
        if (Date.now() - start < 2500) {
          requestAnimationFrame(loop);
        } else {
          stream.getTracks().forEach((t) => t.stop());
          ctx.close().catch(() => {});
          setMicLevel(0);
          setTesting(null);
          setTestResult({ target: 'mic', ok: sawSignal });
        }
      };
      loop();
    } catch (err) {
      console.warn('[DeviceSelector] mic test failed', err);
      setTesting(null);
      setTestResult({ target: 'mic', ok: false });
    }
  }

  async function testOutput(target: 'speaker' | 'ringtone') {
    setTesting(target);
    setTestResult(null);
    const ok =
      target === 'ringtone'
        ? // Exact wat er bij een echt gesprek gebeurt: beltoon-apparaat
          // (of gesprek-speaker als fallback) op het beltoon-volume.
          await playTestTone(ringtoneSpeaker.id ?? speaker.id, ringtoneVolume, 'ring')
        : await playTestTone(speaker.id, 0.3, 'blip');
    setTestResult({ target, ok });
    setTesting(null);
    emitEvent({
      type: 'device_changed',
      source: 'client',
      metadata: { kind: `${target}-test`, ok },
    });
  }

  function handleMicChange(deviceId: string) {
    const device = microphones.find((d) => d.deviceId === deviceId);
    const ref = { id: deviceId || null, label: device?.label ?? null };
    setMicrophone(ref);
    emitEvent({
      type: 'device_changed',
      source: 'client',
      metadata: { kind: 'microphone', id: ref.id, label: ref.label },
    });
  }

  function handleSpeakerChange(deviceId: string) {
    const device = speakers.find((d) => d.deviceId === deviceId);
    const ref = { id: deviceId || null, label: device?.label ?? null };
    setSpeaker(ref);
    emitEvent({
      type: 'device_changed',
      source: 'client',
      metadata: { kind: 'speaker', id: ref.id, label: ref.label },
    });
  }

  function handleRingtoneSpeakerChange(deviceId: string) {
    const device = speakers.find((d) => d.deviceId === deviceId);
    const ref = { id: deviceId || null, label: device?.label ?? null };
    setRingtoneSpeaker(ref);
    emitEvent({
      type: 'device_changed',
      source: 'client',
      metadata: { kind: 'ringtone-speaker', id: ref.id, label: ref.label },
    });
  }

  const sinkIdSupported =
    typeof (HTMLMediaElement.prototype as any).setSinkId === 'function';

  // Gekozen apparaat bestaat niet (meer) in de huidige lijst: waarschuwen in
  // plaats van stil terugvallen op default (= "rinkelt op de computer").
  const speakerMissing =
    speakers.length > 0 &&
    speaker.id !== null &&
    !speakers.some((d) => d.deviceId === speaker.id);
  const ringtoneSpeakerMissing =
    speakers.length > 0 &&
    ringtoneSpeaker.id !== null &&
    !speakers.some((d) => d.deviceId === ringtoneSpeaker.id);

  const effectiveRingtoneLabel =
    ringtoneSpeaker.id !== null
      ? ringtoneSpeaker.label ?? 'gekozen beltoon-apparaat'
      : speaker.id !== null
        ? speaker.label ?? 'gekozen gesprek-speaker'
        : 'systeem-standaard';

  const resultLine = (target: TestTarget, okText: string, failText: string) =>
    testResult?.target === target ? (
      <p
        className={`mt-1 text-xs ${testResult.ok ? 'text-emerald-600' : 'text-red-600'}`}
        role="status"
      >
        {testResult.ok ? okText : failText}
      </p>
    ) : null;

  if (!supported) {
    return (
      <div className="text-sm text-slate-500">
        Je browser ondersteunt geen audio-apparaatselectie.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          "Standaard" volgt het standaardapparaat van je besturingssysteem — dat
          kan wisselen als je een headset (dis)connect. Kies je headset expliciet
          voor een vast apparaat.
        </p>
        <button
          type="button"
          onClick={() => refresh()}
          className="ml-3 shrink-0 px-2 py-1 text-xs text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
          aria-label="Apparaatlijst vernieuwen"
        >
          Vernieuw lijst
        </button>
      </div>

      <div>
        <label
          htmlFor="mic-select"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Microfoon
        </label>
        <div className="flex gap-2">
          <select
            id="mic-select"
            value={microphone.id ?? ''}
            onChange={(e) => handleMicChange(e.target.value)}
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            aria-label="Microfoon selecteren"
          >
            <option value="">Standaard</option>
            {microphones.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Microfoon ${d.deviceId.slice(0, 6)}`}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => testMicrophone(microphone.id)}
            disabled={testing !== null}
            className="px-3 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
            aria-label="Microfoon testen"
          >
            {testing === 'mic' ? 'Testen…' : 'Test'}
          </button>
        </div>
        {testing === 'mic' && (
          <div
            className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(micLevel * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Microfoon niveau"
          >
            <div
              className="h-full bg-indigo-500 transition-[width] duration-75"
              style={{ width: `${Math.min(100, micLevel * 200)}%` }}
            />
          </div>
        )}
        {resultLine(
          'mic',
          'Microfoon werkt — geluid gedetecteerd.',
          'Geen geluid gedetecteerd. Controleer of de microfoon aangesloten is en praat tijdens de test.',
        )}
      </div>

      <div>
        <label
          htmlFor="speaker-select"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Speaker (gesprek)
          {!sinkIdSupported && (
            <span className="ml-2 text-xs text-amber-600">
              (niet ondersteund in deze browser — standaard wordt gebruikt)
            </span>
          )}
        </label>
        <div className="flex gap-2">
          <select
            id="speaker-select"
            value={speaker.id ?? ''}
            onChange={(e) => handleSpeakerChange(e.target.value)}
            disabled={!sinkIdSupported}
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
            aria-label="Speaker selecteren"
          >
            <option value="">Standaard</option>
            {speakers.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Speaker ${d.deviceId.slice(0, 6)}`}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => testOutput('speaker')}
            disabled={testing !== null || !sinkIdSupported}
            className="px-3 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
            aria-label="Speaker testen"
          >
            {testing === 'speaker' ? 'Testen…' : 'Test'}
          </button>
        </div>
        {speakerMissing && (
          <p className="mt-1 text-xs text-amber-600">
            "{speaker.label ?? 'Gekozen apparaat'}" is niet aangesloten — het
            gesprek valt terug op het standaardapparaat. Kies opnieuw.
          </p>
        )}
        {resultLine(
          'speaker',
          'Toon afgespeeld op dit apparaat.',
          'Kon geen geluid op dit apparaat afspelen — het is mogelijk niet (meer) aangesloten. Kies het apparaat opnieuw en test nogmaals.',
        )}
      </div>

      <div>
        <label
          htmlFor="ringtone-speaker-select"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Beltoon via
          <span className="ml-2 text-xs text-slate-500">
            rinkelen op een ander apparaat dan het gesprek (bv. laptopspeakers
            naast je headset)
          </span>
        </label>
        <div className="flex gap-2">
          <select
            id="ringtone-speaker-select"
            value={ringtoneSpeaker.id ?? ''}
            onChange={(e) => handleRingtoneSpeakerChange(e.target.value)}
            disabled={!sinkIdSupported}
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
            aria-label="Beltoon-apparaat selecteren"
          >
            <option value="">Zelfde als gesprek</option>
            {speakers.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Speaker ${d.deviceId.slice(0, 6)}`}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => testOutput('ringtone')}
            disabled={testing !== null || !sinkIdSupported}
            className="px-3 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
            aria-label="Beltoon testen"
          >
            {testing === 'ringtone' ? 'Rinkelt…' : 'Test beltoon'}
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Beltoon klinkt via: {effectiveRingtoneLabel}
        </p>
        {ringtoneSpeakerMissing && (
          <p className="mt-1 text-xs text-amber-600">
            "{ringtoneSpeaker.label ?? 'Gekozen apparaat'}" is niet aangesloten —
            de beltoon valt terug op het standaardapparaat. Kies opnieuw.
          </p>
        )}
        {resultLine(
          'ringtone',
          'Beltoon afgespeeld — precies zo klinkt een inkomend gesprek.',
          'Kon de beltoon niet op dit apparaat afspelen — het is mogelijk niet (meer) aangesloten. Kies het apparaat opnieuw en test nogmaals.',
        )}
      </div>

      <div>
        <label
          htmlFor="ringtone-volume"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Beltoon volume
          <span className="ml-2 text-xs text-slate-500">
            {Math.round(ringtoneVolume * 100)}%
          </span>
        </label>
        <input
          id="ringtone-volume"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={ringtoneVolume}
          onChange={(e) => setRingtoneVolume(Number(e.target.value))}
          className="w-full"
          aria-label="Beltoon volume"
        />
        <p className="mt-1 text-xs text-slate-500">
          Gebruik "Test beltoon" hierboven om het volume op het echte apparaat te
          horen.
        </p>
      </div>
    </div>
  );
}
